import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTopOutlined";
import PercentIcon from "@mui/icons-material/PercentOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali, formatJalaliInstant, formatMoney } from "../lib/format";
import { PageHeader, StatCard } from "../components/ui";

/**
 * Firestore Timestamps are spread straight into the JSON response by
 * `GET /orgs/:orgId/payments/payouts`, so they arrive as { _seconds, … } rather
 * than an ISO string. `periodTo` is a plain "YYYY-MM-DD" and needs no unwrapping.
 */
interface WireTimestamp {
  _seconds: number;
  _nanoseconds?: number;
}

interface Payout {
  id: string;
  periodTo: string;
  grossMinor: number;
  commissionMinor: number;
  commissionPct: number;
  netMinor: number;
  currency: string;
  paymentIds?: string[];
  status: string;
  paidAt?: WireTimestamp | null;
}

function instantOf(ts: WireTimestamp | null | undefined): Date | null {
  return typeof ts?._seconds === "number" ? new Date(ts._seconds * 1000) : null;
}

export function PayoutsPage() {
  const { orgId } = useSession();
  const [payouts, setPayouts] = useState<Payout[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    setError(null);
    api<{ items: Payout[] }>(`/orgs/${orgId}/payments/payouts`)
      .then((r) => setPayouts(r.items))
      .catch((e) => {
        setPayouts([]);
        setError(e instanceof Error ? e.message : "دریافت تسویه‌ها ناموفق بود");
      });
  }, [orgId]);

  useEffect(load, [load]);

  const totals = useMemo(() => {
    const rows = payouts ?? [];
    const sum = (f: (p: Payout) => number, only?: string) =>
      rows.filter((p) => !only || p.status === only).reduce((s, p) => s + f(p), 0);
    return {
      pending: sum((p) => p.netMinor, "pending"),
      paid: sum((p) => p.netMinor, "paid"),
      commission: sum((p) => p.commissionMinor),
    };
  }, [payouts]);

  return (
    <>
      <PageHeader
        title="تسویه‌های پلتفرم"
        subtitle="پرداخت‌های آنلاین مشتریان اول به حساب پلتفرم می‌رود؛ پلتفرم پس از کسر کمیشن، خالص آن را به حساب بانکی تالار انتقال می‌دهد"
        action={
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={!orgId}>
            بازخوانی
          </Button>
        }
      />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<HourglassTopIcon />}
            label="در انتظار پرداخت به شما"
            value={payouts ? formatMoney(totals.pending) : undefined}
            tone="warning"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<AccountBalanceWalletIcon />}
            label="تا حال پرداخت شده"
            value={payouts ? formatMoney(totals.paid) : undefined}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<PercentIcon />}
            label="کمیشن پلتفرم"
            value={payouts ? formatMoney(totals.commission) : undefined}
            hint="از مجموع همهٔ دوره‌ها"
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>دورهٔ تسویه تا</TableCell>
              <TableCell>تعداد پرداخت</TableCell>
              <TableCell>ناخالص</TableCell>
              <TableCell>کمیشن</TableCell>
              <TableCell>خالص پرداختی</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell>تاریخ پرداخت</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(payouts ?? []).map((payout) => {
              const paidAt = instantOf(payout.paidAt);
              return (
                <TableRow key={payout.id} hover>
                  <TableCell>{formatJalali(payout.periodTo)}</TableCell>
                  <TableCell>{payout.paymentIds?.length ?? "—"}</TableCell>
                  <TableCell>{formatMoney(payout.grossMinor, payout.currency)}</TableCell>
                  <TableCell>
                    {formatMoney(payout.commissionMinor, payout.currency)} ({payout.commissionPct}٪)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatMoney(payout.netMinor, payout.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={payout.status === "paid" ? "پرداخت شده" : "در انتظار"}
                      color={payout.status === "paid" ? "success" : "warning"}
                    />
                  </TableCell>
                  <TableCell>{paidAt ? formatJalaliInstant(paidAt) : "—"}</TableCell>
                </TableRow>
              );
            })}
            {payouts !== null && payouts.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    هنوز تسویه‌ای انجام نشده — تسویه وقتی ساخته می‌شود که پرداخت آنلاین
                    تأییدشدهٔ تسویه‌نشده وجود داشته باشد
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {payouts === null && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    در حال بارگذاری…
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        بیعانه‌ها و پرداخت‌های نقدی که در تالار می‌گیرید در این جدول نیستند — آن پول از اول
        نزد شماست و در صفحهٔ «مالی» ثبت می‌شود.
      </Typography>
    </>
  );
}
